import { useState, useCallback, useEffect } from "react";
import {
  ChecklistItemEntity,
  ChecklistProgressEntity,
  GetChecklistProgressUseCase,
} from "../../domain";
import { ChecklistRepositoryImpl } from "../../data/repositories/ChecklistRepositoryImpl";

// Create repository instance (singleton)
const checklistRepository = new ChecklistRepositoryImpl();

// Create use case
const getChecklistProgressUseCase = new GetChecklistProgressUseCase(
  checklistRepository
);

export interface UseChecklistProgressReturn {
  progress: ChecklistProgressEntity | null;
  loading: boolean;
  error: string | null;
  fetchProgress: (month?: string) => Promise<void>;
  refresh: (month?: string) => Promise<void>;
  applyStatusTransition: (
    fromStatus: ChecklistItemEntity["status"],
    toStatus: ChecklistItemEntity["status"]
  ) => void;
  clearError: () => void;
}

const isCompleteStatus = (status: ChecklistItemEntity["status"]): boolean =>
  status === "done" || status === "approved" || status === "to_be_done_later";

const isPendingReviewStatus = (
  status: ChecklistItemEntity["status"]
): boolean => status === "submitted_for_review";

export const useChecklistProgress = (
  month?: string
): UseChecklistProgressReturn => {
  const [progress, setProgress] = useState<ChecklistProgressEntity | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch checklist progress statistics
   */
  const fetchProgress = useCallback(
    async (fetchMonth?: string): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const monthToUse = fetchMonth || month;
        const result = await getChecklistProgressUseCase.execute(monthToUse);
        setProgress(result);
      } catch (err: any) {
        const errorMessage = err.message || "Failed to load checklist progress";
        setError(errorMessage);
        console.error("Error fetching checklist progress:", err);
        
        // Clear progress data on 404 (checklist not found for this month)
        if (err.status === 404) {
          setProgress(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [month]
  );

  /**
   * Refresh progress
   */
  const refresh = useCallback(
    async (refreshMonth?: string): Promise<void> => {
      await fetchProgress(refreshMonth);
    },
    [fetchProgress]
  );

  /**
   * Update progress summary after a successful item status mutation.
   */
  const applyStatusTransition = useCallback(
    (
      fromStatus: ChecklistItemEntity["status"],
      toStatus: ChecklistItemEntity["status"]
    ): void => {
      setProgress((prevProgress) => {
        if (!prevProgress) return prevProgress;

        const wasComplete = isCompleteStatus(fromStatus);
        const isComplete = isCompleteStatus(toStatus);
        const wasPendingReview = isPendingReviewStatus(fromStatus);
        const isPendingReview = isPendingReviewStatus(toStatus);
        const wasRejected = fromStatus === "rejected";
        const isRejected = toStatus === "rejected";

        return {
          ...prevProgress,
          completed_items: Math.max(
            prevProgress.completed_items + Number(isComplete) - Number(wasComplete),
            0
          ),
          approved_items: Math.max(
            prevProgress.approved_items + Number(isComplete) - Number(wasComplete),
            0
          ),
          pending_review_items: Math.max(
            prevProgress.pending_review_items +
              Number(isPendingReview) -
              Number(wasPendingReview),
            0
          ),
          rejected_items: Math.max(
            prevProgress.rejected_items + Number(isRejected) - Number(wasRejected),
            0
          ),
        };
      });
    },
    []
  );

  /**
   * Clear error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset data when month changes
  useEffect(() => {
    setProgress(null);
    setError(null);
  }, [month]);

  return {
    progress,
    loading,
    error,
    fetchProgress,
    refresh,
    applyStatusTransition,
    clearError,
  };
};

